import * as echarts from 'echarts';
import React, { useEffect, useRef, useState, useMemo } from 'react';


const GraphSection = React.memo(({ item, index }: any) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.EChartsType | null>(null);
  const [selectedButton, setSelectedButton] = useState(1);

  // Memoize data for the selected timeframe (prevents re-renders)
  const buttonBasedData = useMemo(() => {
    if (!item?.source) return null;
    return [item?.source?.Daily, item?.source?.Weekly, item?.source?.Monthly][selectedButton - 1];
  }, [selectedButton, item?.source]);

  // Initialize chart only once
  useEffect(() => {
    if (!chartRef.current) return;

    chartInstanceRef.current = echarts.init(chartRef.current);

    const resizeObserver = new ResizeObserver(() => {
      chartInstanceRef.current?.resize();
    });
    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
      chartInstanceRef.current?.dispose();
    };
  }, []);

  // Update chart data only when needed
  useEffect(() => {
    if (!chartInstanceRef.current || !item) return;

    const chart = chartInstanceRef.current;

    const LineChart = {
      xAxis: {
        type: 'category',
        // data: buttonBasedData?.xAxis,
        data: item?.source?.xAxis,
        name: item?.source?.axisName?.xAxis,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: { 
          interval: 0, 
          fontSize: 10, 
          fontWeight: '400',
          rotate: 45,
          formatter: function (value: string) {
            const maxLength = 10;
            return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
          }
        }
      },
      yAxis: {
        type: 'value',
        name: item?.source?.axisName?.yAxis,
        nameLocation: 'middle',
        nameGap: 50,
        nameRotate: 90,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: { 
          fontSize: 14, 
          fontWeight: '400', 
          formatter: item?.name === 'Cost Over Time' ? '€ {value}': function (value:any) {
            if (value >= 1000000000000) return (value / 1000000000000) + 'T';
            if (value >= 1000000000) return (value / 1000000000) + 'B';
            if (value >= 1000000) return (value / 1000000) + 'M';
            if (value >= 1000) return (value / 1000) + 'K';
            return value;
          }
        },
        splitLine: {
          lineStyle: {
            type: "dashed",
            color: "rgba(0, 102, 204, 0.25)"
          }
        },
      },
      series: [
        {
          // data: buttonBasedData?.yAxis,
          data: item?.source?.yAxis,
          type: 'line'
        }
      ],
      title: {
        text: item?.name,
        textStyle: {
          fontSize: 20,
          fontWeight: 400,
          color: "#18181B",
          lineHeight: 16
        },
        left: 0,
        top: 0,
        subtext: item?.description,
        subtextStyle: {
          fontSize: 14,
          color: "#838FB4",
          fontWeight: 400,
          lineHeight: 16,
          width: 700,
          overflow: "break"
        }
      },
      tooltip: {
        trigger: "axis",
      },
      grid: {
        bottom: "15%",
        top: "25%",
      },
    };

    const LineBarChart = {
      xAxis: [
        {
          type: 'category',
          data: item?.source?.xAxis,
          name: item?.source?.axisName?.xAxis,
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
          axisLabel: {
            interval: 0,
            fontSize: 14,
            fontWeight: '400',
            rotate: 45,
            formatter: function (value: string) {
              const maxLength = 10;
              return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
            }
          }
        }
      ],
      yAxis: [
        {
          type: 'value',
          name: item?.source?.axisName?.leftYaxis,
          nameLocation: 'middle',
          nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
          min: 0,
          axisLabel: { fontSize: 14, fontWeight: '400', formatter: '€ {value}' },
          splitLine: {
            lineStyle: {
              type: "dashed",
              color: "rgba(0, 102, 204, 0.25)"
            }
          },
        },
        {
          type: 'value',
          name: item?.source?.axisName?.rightYaxis,
          nameLocation: 'middle',
          nameTextStyle: { fontSize: 14, fontWeight: '400' },
          min: 0,
          axisLabel: { 
            fontSize: 14, 
            fontWeight: '400', 
            formatter: function (value:any) {
              if (value >= 1000000) return (value / 1000000) + 'M';
              if (value >= 1000) return (value / 1000) + 'K';
              return value;
            }
          },
          splitLine: { show: false }
        }
      ],
      series: [
        {
          name: item?.source?.axisName?.leftYaxis,
          type: 'bar',
          itemStyle: { color: 'rgba(0, 102, 204, 1)' },
          data: item?.source?.leftYaxis
        },
        {
          name: item?.source?.axisName?.rightYaxis,
          type: 'line',
          itemStyle: { color: 'rgba(53,76,221,255)' },
          yAxisIndex: 1,
          data: item?.source?.rightYaxis
        }
      ],
      title: {
        text: item?.name,
        textStyle: {
          fontSize: 20,
          fontWeight: 400,
          color: "#18181B",
          lineHeight: 16
        },
        left: 0,
        top: 0,
        subtext: item?.description,
        subtextStyle: {
          fontSize: 14,
          color: "#838FB4",
          fontWeight: 400,
          lineHeight: 16,
          width: 700,
          overflow: "break"
        }
      },
      tooltip: {
        trigger: "axis",
      },
      grid: {
        bottom: "15%",
        top: "25%",
      },
    };

    const donutChart = {
      legend: { bottom: '1%', left: 'center', icon: 'circle' },
      title: {
        text: item?.source?.title,
        subtext: item?.source?.subtitle,
        left: "center",
        top: "45%",
        textStyle: {
          fontSize: 22,
          fontWeight: "bold",
          color: "#000",
        },
        subtextStyle: {
          fontSize: 14,
          color: "#666",
        },
      },
      series: [
        {
          name: 'Access From',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: 'outside',
            formatter: '{c} ({d}%)',
            fontSize: 14,
            color: '#333'
          },
          labelLine: {
            show: true,
            length: 25,
            length2: 15,
            lineStyle: { type: 'solid', color: '#999', width: 1 }
          },
          data: item?.source?.data
        }
      ]
    };

    const onlyBaR = {
      xAxis: {
        type: 'category',
        data: item?.source?.xAxis,
        name: item?.source?.axisName?.xAxis,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: {
          interval: 0,
          // fontSize: 14,
          fontWeight: '400',
          rotate: 45,
          formatter: function (value: string) {
            const maxLength = 10;
            return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
          }
        }
      },
      yAxis: {
        type: 'value',
        name: item?.source?.axisName?.yAxis,
        nameLocation: 'middle',
        // nameGap: 50,
        nameRotate: 90,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: { fontSize: 14, fontWeight: '400', formatter: '€ {value}' },
        splitLine: {
          show: true,
          lineStyle: {
              type: "dashed",
              color: "rgba(0, 102, 204, 0.25)"
          },
        },
      },
      series: [
        {
          data: item?.source?.yAxis,
          type: 'bar',
          color: 'rgba(0, 102, 204, 1)',
          barMaxWidth: 50
        }
      ],
      title: {
        text: item?.name,
        textStyle: {
          fontSize: 20,
          fontWeight: 400,
          color: "#18181B",
          lineHeight: 16
        },
        left: 0,
        top: 0,
        subtext: item?.description,
        subtextStyle: {
          fontSize: 14,
          color: "#838FB4",
          fontWeight: 400,
          lineHeight: 16,
          width: 700,
          overflow: "break"
        }
      },
      tooltip: {
        trigger: "axis",
      },
      grid: {
        bottom: "15%",
        top: "25%",
      },
    };
    const twoBaR = {
      xAxis: {
        type: 'category',
        data: item?.source?.xAxis,
        name: item?.source?.axisName?.xAxis,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: {
          interval: 0,
          // fontSize: 14,
          fontWeight: '400',
          rotate: 45,
          formatter: function (value: string) {
            const maxLength = 10;
            return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
          }
        },
      },
      yAxis: {
        type: 'value',
        name: item?.source?.axisName?.yAxis,
        nameLocation: 'middle',
        // nameGap: 50,
        nameRotate: 90,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: { fontSize: 14, fontWeight: '400', formatter: '€ {value}'},
        splitLine: {
          show: true,
          lineStyle: {
              type: "dashed",
              color: "rgba(0, 102, 204, 0.25)"
          },
        },
      },
      series: item?.source?.yAxis?.map((s:any, index:number) => ({
        name: s?.name,
        type: "bar",
        data: s?.data,
        barMaxWidth: 50,
        itemStyle: {
            color: index === 0 ? "#0066CC" : "#7784F5"
        }
      })) || [],
      title: {
        text: item?.name,
        textStyle: {
          fontSize: 20,
          fontWeight: 400,
          color: "#18181B",
          lineHeight: 16
        },
        left: 0,
        top: 0,
        subtext: item?.description,
        subtextStyle: {
          fontSize: 14,
          color: "#838FB4",
          fontWeight: 400,
          lineHeight: 16,
          width: 700,
          overflow: "break"
        }
      },
      tooltip: {
        trigger: "axis",
      },
      grid: {
        bottom: "30%",
        top: "20%",
      },
      legend: {
        data: item?.source && item?.source?.yAxis?.map((item: any) => item.name),
        bottom: 0,
        itemGap: 20,
        icon: 'circle'
      },
    };
    const horizontalBar = {
      xAxis: {
        type: 'value',
        name: item?.source?.axisName?.xAxis,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: {
          interval: 0,
          // fontSize: 14,
          fontWeight: '400',
        },
        splitLine: {
          show: true,
          lineStyle: {
              type: "dashed",
              color: "rgba(0, 102, 204, 0.25)"
          },
        },
      },
      yAxis: {
        type: 'category',
        data: item?.source?.xAxis,
        inverse: true,
        name: item?.source?.axisName?.yAxis,
        nameLocation: 'middle',
        // nameGap: 50,
        // nameRotate: 90,
        nameTextStyle: { fontSize: 14, fontWeight: '400', color: "#18181B" },
        axisLabel: { 
          fontSize: 14, 
          fontWeight: '400', 
          formatter: function (value: string) {
            const maxLength = 20;
            return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
          }
        },
        splitLine: {
          show: true,
          lineStyle: {
              type: "dashed",
              color: "rgba(0, 102, 204, 0.25)"
          },
        },
      },
      series: [
        {
          type: "bar",
          data: item?.source?.yAxis,
          label: {
              show: true,
              position: "right",
          },
          itemStyle: {
              color: "#0066cc",
          },
          barWidth: "60%",
          barMaxWidth: 50
        },
      ],
      title: {
        text: item?.name,
        textStyle: {
          fontSize: 20,
          fontWeight: 400,
          color: "#18181B",
          lineHeight: 16
        },
        left: 0,
        top: 0,
        subtext: item?.description,
        subtextStyle: {
          fontSize: 14,
          color: "#838FB4",
          fontWeight: 400,
          lineHeight: 16,
          width: 700,
          overflow: "break"
        }
      },
      tooltip: {
        trigger: "axis",
      },
      grid: {
        bottom: "8%",
        // top: "20%",
      },
    };

    const chartOptions = {
      LineBarChart,
      LineChart,
      donutChart,
      onlyBaR,
      twoBaR,
      horizontalBar
    };

    // @ts-ignore
    chart.setOption(chartOptions[item?.graphType] || {}, true);
  }, [buttonBasedData, item]);

  return (
    <div
      style={{
        width: '49%',
        margin: '10px 0px',
        borderRadius: 20,
        padding: 20,
        border: '1px solid var(--analytics_grey)',
        position: 'relative'
      }}>
      {item?.graphType === 'donutChart' &&
        <>
          <p className="m-0" style={{ color: '#18181B', fontSize: '20px' }}>
            {item?.name}
          </p>
          <p className="h5_400" style={{ color: 'var(--grey_tone)' }}>
            {item?.description}
          </p>
        </>
      }
      <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
    </div>
  );
});

export default GraphSection;
